import React, { useState } from 'react';
import { Briefcase, MapPin, Clock, DollarSign, Plus, Tag, Search, Globe, User, Trash2, ShieldX } from 'lucide-react';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useAccountMaturity } from '../hooks/useTrustScore';
import { canCreateMerchantListing, ACCOUNT_AGE } from '../lib/trustScore';
import { isDeletedEvent, markDeleted } from '../components/PostCard';
import { timeAgo } from '../lib/utils';
import { Link } from 'react-router-dom';
import { OPERATOR_PUBKEY } from '../lib/platform';

// ── Operator-hidden jobs (persisted in localStorage) ─────────────
const HIDDEN_JOBS_KEY = 'bchnostr:hidden-jobs';

function getHiddenJobs(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_JOBS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function hideJob(eventId: string) {
  const hidden = getHiddenJobs();
  hidden.add(eventId);
  try { localStorage.setItem(HIDDEN_JOBS_KEY, JSON.stringify([...hidden])); } catch {}
}

function isJobHidden(eventId: string): boolean {
  return getHiddenJobs().has(eventId);
}

const JOB_KIND = 30023;
const JOB_TAG = 'bch-job';
const JOB_TYPES = ['All', 'Full-time', 'Part-time', 'Contract', 'Freelance', 'Remote'];

export default function JobsPage() {
  const { user } = useAppContext();
  const maturity = useAccountMaturity(user?.pubkey);
  const canPost = maturity.isOperator || maturity.loading || (maturity.firstSeen > 0 && canCreateMerchantListing(maturity.firstSeen));
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [jobType, setJobType] = useState('All');

  const { events: jobEvents, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [JOB_KIND], '#t': [JOB_TAG], limit: 50 }],
  });

  const jobs = jobEvents.map(ev => {
    try { return { ...JSON.parse(ev.content), event: ev, pubkey: ev.pubkey }; }
    catch { return { title: 'Untitled Job', event: ev, pubkey: ev.pubkey }; }
  }).filter(j => {
    if (isDeletedEvent(j.event.id)) return false;
    if (isJobHidden(j.event.id)) return false;
    if (searchQuery && !j.title?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (jobType !== 'All' && j.type !== jobType) return false;
    return true;
  });

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary" /> BCH Jobs
          </h1>
          {user && canPost && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" /> Post Job
            </button>
          )}
          {user && !canPost && !maturity.loading && (
            <div className="text-[10px] text-muted-foreground bg-muted rounded-lg px-3 py-1.5 max-w-[180px] text-center">
              Job posting unlocks after {ACCOUNT_AGE.merchantMinDays} days
            </div>
          )}
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search jobs..."
            className="w-full bg-muted rounded-lg pl-10 pr-4 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {JOB_TYPES.map(type => (
            <button
              key={type}
              onClick={() => setJobType(type)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                jobType === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {showForm && <PostJobForm onPosted={() => { setShowForm(false); refetch(); }} />}

      <div className="divide-y divide-border">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No jobs posted yet.</p>
          </div>
        ) : (
          jobs.map((job, i) => <JobCard key={job.event.id || i} job={job} onDeleted={refetch} />)
        )}
      </div>
    </div>
  );
}

function JobCard({ job, onDeleted }: { job: any; onDeleted: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(job.pubkey);
  const [expanded, setExpanded] = useState(false);
  const [deleted, setDeleted] = useState(() => isDeletedEvent(job.event.id));
  const [deleting, setDeleting] = useState(false);

  const isOwnPost = user?.pubkey === job.pubkey;
  const isOperator = user?.pubkey === OPERATOR_PUBKEY;
  const canDelete = isOwnPost || isOperator;

  // Author deletes via NIP-09 kind 5
  const handleDelete = async () => {
    if (!user || !isOwnPost) return;
    if (!confirm('Delete this job posting? This publishes a deletion request to relays.')) return;
    setDeleting(true);
    try {
      const dTag = job.event.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
      const tags: string[][] = [
        ['e', job.event.id],
        ['k', String(JOB_KIND)],
      ];
      if (dTag) {
        tags.push(['a', `${JOB_KIND}:${job.pubkey}:${dTag}`]);
      }
      await publish({
        kind: 5,
        content: 'Deleted by author',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      markDeleted(job.event.id);
      setDeleted(true);
      onDeleted();
    } catch (e) {
      console.error('Delete failed:', e);
    } finally {
      setDeleting(false);
    }
  };

  // Operator removes spam — hides locally + publishes NIP-56 report
  const handleOperatorRemove = async () => {
    if (!user || !isOperator) return;
    if (!confirm('Remove this job as spam? It will be hidden from the directory.')) return;
    setDeleting(true);
    try {
      // Publish NIP-56 report (kind 1984) so other clients can see it
      await publish({
        kind: 1984,
        content: 'Spam/low-quality job listing removed by platform operator',
        tags: [
          ['e', job.event.id, '', 'spam'],
          ['p', job.pubkey, '', 'spam'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      // Hide locally
      hideJob(job.event.id);
      markDeleted(job.event.id);
      setDeleted(true);
      onDeleted();
    } catch (e) {
      console.error('Remove failed:', e);
    } finally {
      setDeleting(false);
    }
  };

  if (deleted) {
    return (
      <div className="px-4 py-3 text-center border-b border-border">
        <p className="text-xs text-muted-foreground italic flex items-center justify-center gap-1.5">
          <Trash2 className="w-3 h-3" /> This job posting has been removed.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 hover:bg-muted/30 transition-colors">
      <div className="flex items-start gap-3">
        <Link to={`/profile/${job.pubkey}`} className="shrink-0">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-10 h-10 rounded-lg object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary/60" />
            </div>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <button onClick={() => setExpanded(!expanded)} className="text-left flex-1 min-w-0">
              <h3 className="font-semibold text-sm hover:text-primary transition-colors">{job.title}</h3>
            </button>
            {canDelete && (
              <button
                onClick={isOwnPost ? handleDelete : handleOperatorRemove}
                disabled={deleting}
                className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                title={isOwnPost ? 'Delete job posting' : 'Remove spam'}
              >
                {isOwnPost ? <Trash2 className="w-3.5 h-3.5" /> : <ShieldX className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{profile?.name || job.company || 'Anonymous'}</p>
          {job.description && (
            <div className="mt-1.5">
              <p className={`text-xs text-muted-foreground whitespace-pre-wrap ${expanded ? '' : 'line-clamp-2'}`}>
                {job.description}
              </p>
              {job.description.length > 120 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-xs text-primary font-medium mt-1 hover:underline"
                >
                  {expanded ? 'Show less' : 'Read full posting'}
                </button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {job.type && (
              <span className="flex items-center gap-1 text-xs bg-primary/5 text-primary px-2 py-0.5 rounded-full">
                <Tag className="w-3 h-3" /> {job.type}
              </span>
            )}
            {job.location && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="w-3 h-3" /> {job.location}
              </span>
            )}
            {job.salary && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <DollarSign className="w-3 h-3" /> {job.salary}
              </span>
            )}
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" /> {timeAgo(job.event.created_at)}
            </span>
          </div>
          {job.payInBCH && (
            <span className="inline-flex items-center gap-1 mt-2 text-xs bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded-full font-medium">
              Pays in BCH
            </span>
          )}

          {expanded && (
            <div className="mt-3 pt-3 border-t border-border">
              {job.contactEmail && (
                <a href={`mailto:${job.contactEmail}`} className="flex items-center gap-1.5 text-xs text-primary hover:underline">
                  <Globe className="w-3 h-3" /> {job.contactEmail}
                </a>
              )}
              {job.contactUrl && (
                <a href={job.contactUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-primary hover:underline mt-1">
                  <Globe className="w-3 h-3" /> {job.contactUrl}
                </a>
              )}
              <Link
                to={`/profile/${job.pubkey}`}
                className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary font-medium hover:underline"
              >
                <User className="w-3 h-3" /> Contact poster
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PostJobForm({ onPosted }: { onPosted: () => void }) {
  const { publish } = useNostrPublish();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('Remote');
  const [location, setLocation] = useState('');
  const [salary, setSalary] = useState('');
  const [payInBCH, setPayInBCH] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await publish({
        kind: JOB_KIND,
        content: JSON.stringify({ title, description, type, location, salary, payInBCH }),
        tags: [['t', JOB_TAG], ['d', `job-${Date.now()}`], ['title', title]],
        created_at: Math.floor(Date.now() / 1000),
      });
      onPosted();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="border-b border-border p-4 space-y-3 bg-muted/30">
      <h3 className="font-semibold text-sm">Post a Job</h3>
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Job title" className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary" required />
      <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Job description..." className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none resize-none" rows={3} />
      <div className="grid grid-cols-3 gap-3">
        <select value={type} onChange={e => setType(e.target.value)} className="bg-background rounded-lg px-3 py-2 text-sm border border-border">
          {JOB_TYPES.filter(t => t !== 'All').map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Location" className="bg-background rounded-lg px-3 py-2 text-sm border border-border" />
        <input value={salary} onChange={e => setSalary(e.target.value)} placeholder="Salary/Rate" className="bg-background rounded-lg px-3 py-2 text-sm border border-border" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={payInBCH} onChange={e => setPayInBCH(e.target.checked)} className="rounded border-border" />
        Pays in BCH
      </label>
      <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
        {saving ? 'Publishing...' : 'Publish Job'}
      </button>
    </form>
  );
}

